import { memo, useEffect, useState } from "react";

import {
  APIProvider,
  AdvancedMarker,
  Map,
  MapCameraChangedEvent,
  Pin,
} from "@vis.gl/react-google-maps";

import { DEFAULT_POSITION, GOOGLE_MAPS_API_KEY } from "@zenith/data/constants";
import { Marker } from "./interfaces";

export interface GMapsComponentProps {
  currentPosition: google.maps.LatLngLiteral | null;
  markers: Marker[];
}

const ZOOM_LEVEL = 14;

const LocationMarkers: React.NamedExoticComponent<{ markers: Marker[] }> = memo(
  function LocationMarkers(props: { markers: Marker[] }): JSX.Element {
    return (
      <>
        {props.markers.map((marker: Marker) => (
          <AdvancedMarker key={marker.key} position={marker.location}>
            <Pin />
          </AdvancedMarker>
        ))}
      </>
    );
  }
);

const GMapsComponent: React.NamedExoticComponent<GMapsComponentProps> = memo(
  function GMapsComponent(props: GMapsComponentProps): JSX.Element {
    const { currentPosition, markers } = props;
    const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>(DEFAULT_POSITION);
    const [mapZoom, setMapZoom] = useState<number>(12);
    const [isGoogleMapsAvailable, setIsGoogleMapsAvailable] = useState(false);

    useEffect(() => {
      // Runtime check for Google Maps availability
      if (typeof google !== 'undefined' && google.maps) {
        setIsGoogleMapsAvailable(true);
      } else {
        console.warn('Google Maps API not available');
      }
    }, []);

    useEffect(() => {
      if (currentPosition) {
        setMapCenter(currentPosition);
        setMapZoom(ZOOM_LEVEL);
      }
    }, [currentPosition]);

    // Used only for debugging
    function handleCameraChange(ev: MapCameraChangedEvent): void {
      console.info("camera: ", ev.detail.center, "zoom: ", ev.detail.zoom);
    }

    // Used only for debugging
    function handleMapOnLoad(): void {
      console.info("Maps API has loaded.");
    }

    // Fallback UI if Google Maps is not available
    if (!isGoogleMapsAvailable) {
      return (
        <div style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5"
        }}>
          <div>Loading maps...</div>
        </div>
      );
    }

    return (
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY} onLoad={handleMapOnLoad}>
        <Map
          style={{ width: "100%", height: "100%" }}
          center={mapCenter}
          zoom={mapZoom}
          mapId="return-pos-device-map"
          gestureHandling={"greedy"}
          disableDefaultUI
          onCameraChanged={handleCameraChange}
        >
          <LocationMarkers markers={markers} />
        </Map>
      </APIProvider>
    );
  }
);

export default GMapsComponent;
